import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ConferenceApplicationDetailsResponse } from "../../interfaces/conference-applications/ConferenceApplicationsDetailsResponse";
import { ConferenceApplicationResponse } from "../../interfaces/conference-applications/ConferenceApplicationResponse";
import { ConfirmConferenceApplicationDeletionResponse } from "../../interfaces/conference-applications/ConfirmConferenceApplicationDeletionResponse";
import { AxiosResponse } from "axios";
import { SetIsFeaturedOnParticipantsListRequest } from "../../interfaces/conference-applications/SetIsFeaturedOnParticipantsListRequest";
import { AN_ERROR_OCCURRED_MESSAGE } from "../../utils/constants";
import conferenceApplicationsService from "../../services/conferenceApplicationsService";

interface ConferenceApplicationsState {
  conferenceApplications: ConferenceApplicationResponse[];
  conferenceApplicationsWithDeleted: ConferenceApplicationDetailsResponse[];
  loading: boolean;
  error: string | null;
}

const conferenceApplicationsInitialState: ConferenceApplicationsState = {
  conferenceApplications: [],
  conferenceApplicationsWithDeleted: [],
  loading: false,
  error: null,
};

export const fetchConferenceApplications = createAsyncThunk<
  ConferenceApplicationResponse[],
  void,
  { rejectValue: string }
>("conferenceApplications/fetchAll", async (_, { rejectWithValue }) => {
  try {
    const allConferenceApplications = await conferenceApplicationsService.getAllConferenceApplications();
    return allConferenceApplications;
  } catch (error: any) {
    return rejectWithValue(error.message || AN_ERROR_OCCURRED_MESSAGE);
  }
});

export const fetchConferenceApplicationsWithDeleted = createAsyncThunk<
  ConferenceApplicationDetailsResponse[],
  void,
  { rejectValue: string }
>(
  "conferenceApplications/fetchAllWithDeleted",
  async (_, { rejectWithValue }) => {
    try {
      const allConferenceApplicationsWithDeleted = await conferenceApplicationsService.getAllConferenceApplicationsWithDeleted();
      return allConferenceApplicationsWithDeleted;
    } catch (error: any) {
      return rejectWithValue(error.message || AN_ERROR_OCCURRED_MESSAGE);
    }
  }
);

export const createConferenceApplication = createAsyncThunk<
  AxiosResponse<any, any>,
  FormData,
  { rejectValue: string }
>("conferenceApplications/create", async (formData, { rejectWithValue }) => {
  try {
    const createdConferenceApplication = await conferenceApplicationsService.applyToConference(formData);
    return createdConferenceApplication.data;
  } catch (error: any) {
    return rejectWithValue(error.response.data);
  }
});

export const deleteConferenceApplication = createAsyncThunk<
  ConferenceApplicationDetailsResponse,
  string,
  { rejectValue: string }
>("conferenceApplications/delete", async (id, { rejectWithValue }) => {
  try {
    const deletedConferenceApplication = await conferenceApplicationsService.deleteConferenceApplication(id);
    return deletedConferenceApplication;
  } catch (error: any) {
    return rejectWithValue(error.message || AN_ERROR_OCCURRED_MESSAGE);
  }
});

export const confirmConferenceApplicationDeletion = createAsyncThunk<
  ConfirmConferenceApplicationDeletionResponse,
  string,
  { rejectValue: string }
>(
  "conferenceApplications/confirm-deletion",
  async (id, { rejectWithValue }) => {
    try {
      const hardDeletedConferenceApplication = await conferenceApplicationsService.confirmConferenceApplicationDeletion(id);
      return hardDeletedConferenceApplication;
    } catch (error: any) {
      return rejectWithValue(error.message || AN_ERROR_OCCURRED_MESSAGE);
    }
  }
);

export const restoreConferenceApplication = createAsyncThunk<
  ConferenceApplicationDetailsResponse,
  string,
  { rejectValue: string }
>("conferenceApplications/restore", async (id, { rejectWithValue }) => {
  try {
    const restoredConferenceApplication = await conferenceApplicationsService.restoreConferenceApplication(id);
    return restoredConferenceApplication;
  } catch (error: any) {
    return rejectWithValue(error.message || AN_ERROR_OCCURRED_MESSAGE);
  }
});

export const setIsFeaturedOnParticipantsList = createAsyncThunk<
  ConferenceApplicationResponse,
  {
    id: string;
    setIsFeaturedOnParticipantsListRequest: SetIsFeaturedOnParticipantsListRequest;
  },
  { rejectValue: string }
>(
  "conferenceApplications/set-is-featured-on-participants-list",
  async (
    { id, setIsFeaturedOnParticipantsListRequest },
    { rejectWithValue }
  ) => {
    try {
      const conferenceApplicationFeaturedOnParticipantsList =
        await conferenceApplicationsService.setIsFeaturedOnParticipantsList(
          id,
          setIsFeaturedOnParticipantsListRequest
        );
      return conferenceApplicationFeaturedOnParticipantsList;
    } catch (error: any) {
      return rejectWithValue(error.message || "");
    }
  }
);

export const getAbstractFiles = createAsyncThunk<string[], string, { rejectValue: string }>(
  "conferenceApplications/get-abstract-files",
  async (id, { rejectWithValue }) => {
    try {
      const retrievedAbstractFiles = await conferenceApplicationsService.getAbstractFiles(id);
      return retrievedAbstractFiles;
    } catch (error: any) {
      return rejectWithValue(error.message || "");
    }
  }
);

const conferenceApplicationSlice = createSlice({
  name: "conferenceApplications",
  initialState: conferenceApplicationsInitialState,
  reducers: {},
  extraReducers: (actionReducerMapBuilder) => {
    actionReducerMapBuilder
      .addCase(createConferenceApplication.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createConferenceApplication.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createConferenceApplication.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchConferenceApplications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConferenceApplications.fulfilled, (state, action) => {
        state.conferenceApplications = action.payload;
        state.loading = false;
      })
      .addCase(fetchConferenceApplications.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = false;
      })
      .addCase(fetchConferenceApplicationsWithDeleted.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConferenceApplicationsWithDeleted.fulfilled, (state, action) => {
        state.conferenceApplicationsWithDeleted = action.payload;
        state.loading = false;
      })
      .addCase(fetchConferenceApplicationsWithDeleted.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = false;
      })
      .addCase(deleteConferenceApplication.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteConferenceApplication.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteConferenceApplication.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = false;
      })
      .addCase(confirmConferenceApplicationDeletion.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(confirmConferenceApplicationDeletion.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(confirmConferenceApplicationDeletion.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = false;
      })
      .addCase(restoreConferenceApplication.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(restoreConferenceApplication.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(restoreConferenceApplication.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(setIsFeaturedOnParticipantsList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(setIsFeaturedOnParticipantsList.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(setIsFeaturedOnParticipantsList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getAbstractFiles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAbstractFiles.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(getAbstractFiles.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = false;
      });
  },
});

export default conferenceApplicationSlice.reducer;